import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Facebook, CheckCircle2, XCircle, AlertTriangle, Clock, Send, RefreshCw, ExternalLink, Filter, Eye, Share2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "en_attente", label: "À valider", cls: "bg-amber-500 text-white" },
  { key: "a_modifier", label: "À modifier", cls: "bg-orange-500 text-white" },
  { key: "validee",    label: "Validées",  cls: "bg-emerald-500 text-white" },
  { key: "programmee", label: "Programmées", cls: "bg-blue-500 text-white" },
  { key: "publiee",    label: "Publiées",  cls: "bg-slate-700 text-white" },
  { key: "refusee",    label: "Refusées",  cls: "bg-rose-500 text-white" },
  { key: "all",        label: "Tout",      cls: "bg-slate-200 text-slate-700" },
];

function StatusBadge({ status }) {
  const map = {
    en_attente: { l: "En attente", c: "bg-amber-100 text-amber-800", I: Clock },
    a_modifier: { l: "À modifier", c: "bg-orange-100 text-orange-800", I: AlertTriangle },
    validee:    { l: "Validée",    c: "bg-emerald-100 text-emerald-800", I: CheckCircle2 },
    programmee: { l: "Programmée", c: "bg-blue-100 text-blue-800", I: Clock },
    publiee:    { l: "Publiée",    c: "bg-emerald-600 text-white", I: CheckCircle2 },
    refusee:    { l: "Refusée",    c: "bg-rose-100 text-rose-800", I: XCircle },
    annulee:    { l: "Annulée",    c: "bg-slate-200 text-slate-600", I: XCircle },
    brouillon:  { l: "Brouillon",  c: "bg-slate-100 text-slate-700", I: Clock },
  };
  const s = map[status] || { l: status, c: "bg-slate-100 text-slate-700", I: Clock };
  const I = s.I;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${s.c}`}><I size={11}/> {s.l}</span>;
}

export default function AdminFbPosts({ onlyOffers = false }) {
  const [tab, setTab] = useState("en_attente");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x)=>x!==id) : [...s, id]);
  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer ${selected.length} publication(s) ? Cette action est définitive.`)) return;
    try {
      const { data } = await api.post("/admin/fb-posts/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} publication(s) supprimée(s).`);
      setSelected([]); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de suppression."); }
  };
  const [fb, setFb] = useState(null);
  const [lookupCode, setLookupCode] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const doLookup = async () => {
    const code = lookupCode.trim().toUpperCase();
    if (!code) return;
    setLookupBusy(true);
    try {
      const { data } = await api.get(`/admin/fb-posts/lookup?code=${encodeURIComponent(code)}`);
      setLookupResult(data.items || []);
    } catch (e) { toast.error("Erreur de recherche."); } finally { setLookupBusy(false); }
  };

  const loadFb = async () => {
    try { const r = await api.get("/admin/facebook/status"); setFb(r.data); } catch (e) { /* noop */ }
  };
  useEffect(() => { loadFb(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("fb");
    if (!p) return;
    const map = {
      connected: ["success", "Page Facebook connectée ✅"],
      nopage: ["error", "Aucune Page administrée trouvée pour ce compte Facebook."],
      cancelled: ["warning", "Connexion Facebook annulée."],
      badstate: ["error", "Session expirée, relancez la connexion."],
      error: ["error", "Échec de la connexion Facebook. Vérifiez la configuration Meta."],
    };
    const m = map[p];
    if (m) { (toast[m[0]] || toast)(m[1]); }
    window.history.replaceState({}, "", window.location.pathname);
    loadFb();
    // eslint-disable-next-line
  }, []);
  const connectFb = async () => {
    try { const r = await api.get("/admin/facebook/connect-url"); window.location.href = r.data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Configuration Meta manquante."); }
  };
  const disconnectFb = async () => {
    try { await api.post("/admin/facebook/disconnect"); toast.success("Page Facebook déconnectée."); loadFb(); }
    catch (e) { toast.error("Erreur."); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [list, c, g] = await Promise.all([
        api.get(`/admin/fb-posts${tab && tab !== "all" ? `?status=${tab}` : ""}`),
        api.get("/admin/fb-posts/counts"),
        api.get("/admin/facebook-groups"),
      ]);
      setItems(list.data || []);
      setCounts(c.data || {});
      setGroups(g.data.items || []);
    } catch (e) {
      toast.error("Erreur de chargement.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const patch = async (pid, body) => {
    setBusy(pid);
    try {
      const { data } = await api.patch(`/admin/fb-posts/${pid}`, body);
      if (body.action === "validate") {
        if (data.published_to_facebook) {
          toast.success("Validée et publiée sur la page Facebook PROXIreduc-28 ✅");
        } else {
          toast.warning("Validée, mais non publiée sur Facebook : " + (data.facebook_error || "jeton non configuré"));
        }
      } else {
        toast.success("Action enregistrée.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(""); }
  };

  const totalToProcess = (counts.en_attente || 0) + (counts.a_modifier || 0);
  const shown = onlyOffers ? items.filter((p) => p.offer_text || p.offer_id) : items;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-fbposts-title">
            <Facebook className="text-blue-600" size={26}/> {onlyOffers ? "Publications des offres" : "Publications Facebook"}
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">{onlyOffers ? "Validez et partagez les publications d'offres soumises par les partenaires." : "Validez les publications soumises par les partenaires puis programmez/publiez-les sur les groupes."}</p>
        </div>
        <div className="flex items-center gap-2">
          {totalToProcess > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-bold" data-testid="admin-fbposts-badge">
              <AlertTriangle size={12}/> {totalToProcess} à traiter
            </span>
          )}
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-fbposts-reload">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser
          </Button>
        </div>
      </div>

      <FbConnectionCard fb={fb} onConnect={connectFb} onDisconnect={disconnectFb}/>

      {/* Vérification d'origine « vient de PROXIreduc ? » par code de référence */}
      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-fbposts-lookup">
        <div className="text-sm font-bold text-slate-900 flex items-center gap-2"><Facebook size={16} className="text-[#E05638]"/> Vérifier l'origine d'une publication</div>
        <p className="text-xs text-slate-500 mt-0.5">Collez le code de référence (ex. PR-7K2QF) lu sur un post Facebook pour confirmer qu'il a été diffusé via PROXIreduc.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Input value={lookupCode} onChange={(e)=>setLookupCode(e.target.value)} placeholder="PR-XXXXX" className="max-w-[200px] font-mono" data-testid="admin-fbposts-lookup-input"
                 onKeyDown={(e)=>{ if (e.key === "Enter") doLookup(); }}/>
          <Button onClick={doLookup} disabled={lookupBusy} className="btn-shine text-white" data-testid="admin-fbposts-lookup-btn">Vérifier</Button>
        </div>
        {lookupResult !== null && (
          <div className="mt-3" data-testid="admin-fbposts-lookup-result">
            {lookupResult.length === 0 ? (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">Aucune publication avec ce code — ce post ne provient pas de PROXIreduc (ou le code est erroné).</div>
            ) : lookupResult.map((r) => (
              <div key={r.id} className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                ✅ <b>{r.business_name || "—"}</b> · {r.title} · publié le {new Date(r.published_at || r.created_at).toLocaleString("fr-FR")} (réf. {r.ref_code})
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="admin-fbposts-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={()=>setTab(t.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 border transition ${tab === t.key ? t.cls + " border-transparent shadow" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                  data-testid={`admin-fbposts-tab-${t.key}`}>
            {t.label}
            <span className="ml-1 opacity-90">{t.key === "all" ? Object.values(counts).reduce((a,b)=>a+b,0) : (counts[t.key] || 0)}</span>
          </button>
        ))}
      </div>

      {selected.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2" data-testid="admin-fbposts-bulk-bar">
          <span className="text-sm font-semibold text-rose-800">{selected.length} sélectionnée(s)</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={()=>setSelected([])}>Annuler</Button>
            <Button size="sm" onClick={bulkDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-fbposts-bulk-delete">
              <Trash2 size={13} className="mr-1"/> Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-slate-500 text-sm mt-6">Chargement…</div>
      ) : shown.length === 0 ? (
        <div className="mt-6 text-slate-500 text-sm bg-white border border-dashed rounded-2xl p-8 text-center">Aucune publication dans cette catégorie.</div>
      ) : (
        <div className="mt-4 space-y-3">
          {shown.map((p) => (
            <PostCard key={p.id} p={p} groups={groups} expanded={expandedId === p.id}
                      selected={selected.includes(p.id)} onSelect={()=>toggleSel(p.id)}
                      onToggle={()=>setExpandedId((v)=>v === p.id ? "" : p.id)}
                      onAction={(body)=>patch(p.id, body)} busy={busy === p.id}/>
          ))}
        </div>
      )}
    </div>
  );
}

function FbConnectionCard({ fb, onConnect, onDisconnect }) {
  if (!fb) return null;
  const connected = fb.connected && fb.status !== "reconnect_required";
  const needReconnect = fb.status === "reconnect_required";
  return (
    <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="fb-connection-card">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${connected ? "bg-emerald-50" : needReconnect ? "bg-amber-50" : "bg-slate-100"}`}>
          <Facebook className={connected ? "text-emerald-600" : needReconnect ? "text-amber-600" : "text-slate-400"} size={20}/>
        </div>
        <div className="min-w-0">
          {connected ? (
            <>
              <div className="font-bold text-slate-900 text-sm flex items-center gap-1"><CheckCircle2 size={14} className="text-emerald-600"/> Page Facebook connectée</div>
              <div className="text-xs text-slate-500 truncate">{fb.page_name || fb.page_id} — publication automatique active à la validation</div>
            </>
          ) : needReconnect ? (
            <>
              <div className="font-bold text-amber-700 text-sm flex items-center gap-1"><AlertTriangle size={14}/> Reconnexion Facebook requise</div>
              <div className="text-xs text-slate-500 truncate">{fb.last_error || "L'autorisation Facebook a expiré."}</div>
            </>
          ) : (
            <>
              <div className="font-bold text-slate-900 text-sm">Publication automatique Facebook</div>
              <div className="text-xs text-slate-500">{fb.app_configured ? "Connectez votre page pour publier automatiquement dès validation." : "Configuration Meta requise (identifiant + secret d'application)."}</div>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {connected ? (
          <Button size="sm" variant="outline" onClick={onDisconnect} data-testid="fb-disconnect-btn">Déconnecter</Button>
        ) : (
          <Button size="sm" onClick={onConnect} disabled={!fb.app_configured} className="btn-shine text-white" data-testid="fb-connect-btn">
            <Facebook size={14} className="mr-1"/> {needReconnect ? "Reconnecter Facebook" : "Connecter ma page Facebook"}
          </Button>
        )}
      </div>
    </div>
  );
}

function ShareBar({ p }) {
  const text = [p.title, p.text].filter(Boolean).join("\n\n");
  const url = p.fb_post_url || p.destination_url || "";
  const enc = encodeURIComponent;
  const combined = [text, url].filter(Boolean).join("\n");
  const wa = `https://wa.me/?text=${enc(combined)}`;
  const mail = `mailto:?subject=${enc(p.title || "Publication PROXIreduc-28")}&body=${enc(combined)}`;
  const fbShare = url ? `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` : "";
  const x = `https://twitter.com/intent/tweet?text=${enc(text)}${url ? `&url=${enc(url)}` : ""}`;
  const copy = async () => { try { await navigator.clipboard.writeText(combined); toast.success("Copié !"); } catch { toast.message("Copie manuelle requise."); } };
  const native = async () => { if (navigator.share) { try { await navigator.share({ title: p.title, text, url: url || undefined }); } catch (e) { /* noop */ } } else copy(); };
  return (
    <div className="w-full mt-1 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2" data-testid={`admin-fbposts-share-${p.id}`}>
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest inline-flex items-center gap-1"><Share2 size={12}/> Partager :</span>
      <a href={wa} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded-lg bg-green-50 text-green-700 font-semibold hover:bg-green-100" data-testid={`share-wa-${p.id}`}>WhatsApp</a>
      <a href={mail} className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200" data-testid={`share-mail-${p.id}`}>Email</a>
      {fbShare && <a href={fbShare} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100" data-testid={`share-fb-${p.id}`}>Facebook</a>}
      <a href={x} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-900 font-semibold hover:bg-slate-200" data-testid={`share-x-${p.id}`}>X</a>
      <button onClick={native} className="text-xs px-2 py-1 rounded-lg bg-[#E05638] text-white font-semibold hover:opacity-90" data-testid={`share-native-${p.id}`}>Plus / Copier</button>
    </div>
  );
}

function PostCard({ p, groups, expanded, onToggle, onAction, busy, selected, onSelect }) {
  const [comment, setComment] = useState(p.admin_comment || "");
  const [scheduledAt, setScheduledAt] = useState((p.scheduled_at || "").slice(0, 16));
  const [fbUrl, setFbUrl] = useState(p.fb_post_url || "");
  const [selectedGroups, setSelectedGroups] = useState(p.target_groups || []);

  const toggleGroup = (id) => {
    setSelectedGroups((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const businessName = p.business_name || "—";
  const plan = ({gratuit:"Gratuit", essentiel:"Essentiel", visibilite:"Visibilité", premium:"Premium", illimite:"Illimité"})[p.plan_key] || p.plan_key;
  const lateDays = (() => {
    if (p.status !== "a_modifier") return null;
    const ts = p.a_modifier_at || p.updated_at;
    if (!ts) return null;
    const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    return d >= 3 ? d : null;
  })();

  return (
    <div className={`bg-white border rounded-2xl overflow-hidden ${lateDays !== null ? "border-rose-400 shadow-sm" : p.status === "en_attente" ? "border-amber-300 shadow-sm" : "border-slate-200"}`} data-testid={`admin-fbposts-item-${p.id}`}>
      <div className="p-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="checkbox" checked={!!selected} onChange={onSelect} onClick={(e)=>e.stopPropagation()} className="w-4 h-4 accent-[#E05638] cursor-pointer" data-testid={`admin-fbposts-select-${p.id}`}/>
            <StatusBadge status={p.status}/>
            {lateDays !== null && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-600 text-white animate-pulse" data-testid={`admin-fbposts-late-${p.id}`}>
                <AlertTriangle size={11}/> En retard · {lateDays} j
              </span>
            )}
            <span className="text-xs font-bold text-slate-900">{businessName}</span>
            {p.ref_code && <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-orange-50 text-[#E05638] border border-orange-200" data-testid={`admin-fbposts-ref-${p.id}`}>{p.ref_code}</span>}
            <Link to={`/admin/publications-facebook/${p.id}`} onClick={(e)=>e.stopPropagation()} className="ml-auto text-xs px-2.5 py-1 rounded-lg bg-[#E05638] text-white font-semibold inline-flex items-center gap-1 hover:opacity-90" data-testid={`admin-fbposts-open-${p.id}`}>
              Aperçu &amp; partage
            </Link>
            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase">{plan}</span>
            <span className="text-[11px] text-slate-500">Soumis {new Date(p.submitted_at || p.created_at).toLocaleString("fr-FR")}</span>
          </div>
          <div className="mt-1 font-heading font-bold text-slate-900 line-clamp-1">{p.title}</div>
          <div className="text-sm text-slate-600 line-clamp-2 whitespace-pre-line">{p.text}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onToggle} data-testid={`admin-fbposts-expand-${p.id}`}>
            <Eye size={13} className="mr-1"/> {expanded ? "Réduire" : "Détails"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-3 border border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Contenu proposé</div>
              <div className="mt-1 whitespace-pre-line text-sm">{p.text}</div>
              <div className="mt-2 text-[11px] text-slate-500 grid grid-cols-2 gap-x-3 gap-y-1">
                {p.phone && <div>Tél : <b>{p.phone}</b></div>}
                {p.website && <div>Site : <b>{p.website}</b></div>}
                {p.address && <div className="col-span-2">Adresse : <b>{p.address}</b></div>}
                {p.destination_url && <div className="col-span-2">Lien : <b>{p.destination_url}</b></div>}
                {p.offer_text && <div className="col-span-2">Offre : <b>{p.offer_text}</b></div>}
                {p.desired_date && <div>Date souhaitée : <b>{p.desired_date}</b></div>}
                {p.extra && <div className="col-span-2">Notes : {p.extra}</div>}
              </div>
              {(p.image_url || p.image_secondary_url) && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {p.image_url && <img src={p.image_url} alt="" className="w-full h-24 object-cover rounded-lg border"/>}
                  {p.image_secondary_url && <img src={p.image_secondary_url} alt="" className="w-full h-24 object-cover rounded-lg border"/>}
                </div>
              )}
              {(p.media || []).length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`admin-fbposts-media-${p.id}`}>
                  {(p.media || []).map((m, i) => m.type === "video"
                    ? <video key={m.url || i} src={m.url} controls className="w-full h-28 object-cover rounded-lg border bg-black"/>
                    : <img key={m.url || i} src={m.url} alt="" className="w-full h-28 object-cover rounded-lg border"/>)}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="bg-white rounded-xl p-3 border border-slate-200">
                <Label className="text-xs">Commentaire à l'auteur (obligatoire pour "demander modification")</Label>
                <Textarea rows={2} value={comment} onChange={(e)=>setComment(e.target.value)} data-testid={`admin-fbposts-comment-${p.id}`}/>
              </div>
              <div className="bg-white rounded-xl p-3 border border-slate-200">
                <Label className="text-xs flex items-center gap-1"><Filter size={12}/> Groupes ciblés ({selectedGroups.length}/{groups.length})</Label>
                <div className="mt-2 max-h-40 overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-1 pr-1">
                  {groups.map((g) => (
                    <label key={g.id} className="flex items-center gap-1 text-[11px] cursor-pointer">
                      <input type="checkbox" checked={selectedGroups.includes(g.id)} onChange={()=>toggleGroup(g.id)} data-testid={`admin-fbposts-group-${g.id}`}/>
                      <span className="truncate">{g.name || g.url}</span>
                    </label>
                  ))}
                  {groups.length > 0 && (
                    <button type="button" onClick={()=>setSelectedGroups(selectedGroups.length === groups.length ? [] : groups.map((x)=>x.id))}
                            className="text-blue-700 text-[11px] font-semibold text-left mt-1">
                      {selectedGroups.length === groups.length ? "Tout désélectionner" : "Tout sélectionner"}
                    </button>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Programmer pour le</Label>
                  <Input type="datetime-local" value={scheduledAt} onChange={(e)=>setScheduledAt(e.target.value)} data-testid={`admin-fbposts-scheduled-${p.id}`}/>
                </div>
                <div>
                  <Label className="text-xs">URL publication Facebook (si publiée)</Label>
                  <Input value={fbUrl} onChange={(e)=>setFbUrl(e.target.value)} placeholder="https://www.facebook.com/…" data-testid={`admin-fbposts-fburl-${p.id}`}/>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={()=>onAction({ action: "reject", admin_comment: comment })} className="text-rose-600 hover:bg-rose-50" data-testid={`admin-fbposts-reject-${p.id}`}>
              <XCircle size={13} className="mr-1"/> Refuser
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={()=>onAction({ action: "request_changes", admin_comment: comment })} data-testid={`admin-fbposts-changes-${p.id}`}>
              <AlertTriangle size={13} className="mr-1 text-orange-600"/> Demander modif
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={()=>onAction({ action: "validate", admin_comment: comment })} className="text-emerald-700 hover:bg-emerald-50" data-testid={`admin-fbposts-validate-${p.id}`}>
              <CheckCircle2 size={13} className="mr-1 text-emerald-600"/> Valider &amp; publier
            </Button>
            <Button size="sm" variant="outline" disabled={busy || !scheduledAt} onClick={()=>onAction({ action: "schedule", scheduled_at: new Date(scheduledAt).toISOString(), target_groups: selectedGroups })} data-testid={`admin-fbposts-schedule-${p.id}`}>
              <Clock size={13} className="mr-1 text-blue-600"/> Programmer
            </Button>
            <Button size="sm" disabled={busy} onClick={()=>onAction({ action: "publish", fb_post_url: fbUrl, target_groups: selectedGroups })} className="btn-shine text-white" data-testid={`admin-fbposts-publish-${p.id}`}>
              <Send size={13} className="mr-1"/> Marquer publiée
            </Button>
            {p.fb_post_url && (
              <a href={p.fb_post_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold ml-2">
                Voir <ExternalLink size={11}/>
              </a>
            )}
          </div>
          {["validee", "programmee", "publiee"].includes(p.status) && <ShareBar p={p}/>}
        </div>
      )}
    </div>
  );
}
